import React, { useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import colors from 'assets/scss/colors.module.scss';
import { ECharts, EChartsOption, init } from 'echarts';
import { debounce } from 'lodash';

interface IProgressRing {
	value: number;
	total: number;
	max?: number;
	color?: string;
	text?: string | number;
	label?: string;
	classModifier?: string;
}

export const ProgressRing: React.FC<IProgressRing> = observer((props: IProgressRing) => {
	const SIZE = 50;
	const chartRef = useRef<HTMLDivElement>(null);

	const ziel = props.max || props.total;

	useEffect(() => {
		let option: EChartsOption = {
			tooltip: {
				show: false,
			},
			legend: {
				show: false,
			},
			itemStyle: {
				borderRadius: 10,
			},
			emphasis: {
				disabled: true,
			},
			series: [
				{
					name: 'progress',
					type: 'pie',
					radius: ['70%', '90%'],
					labelLine: {
						show: false,
					},
					data: [
						{ value: props.value, itemStyle: { color: props.color || colors.blue } },
						{ value: ziel - props.value, itemStyle: { color: colors.border } },
					],
				},
			],
		};

		let chart: ECharts | null = null;
		if (chartRef.current !== null) {
			chart = init(chartRef.current, null, { renderer: 'svg' });
			chart.setOption(option);
		}

		const handleResize = debounce(() => {
			chart?.resize();
		}, 300);
		window.addEventListener('resize', handleResize);

		return () => {
			chart?.dispose();
			window.removeEventListener('resize', handleResize.cancel);
		};
	}, [chartRef, props.value, props.total, props.color]);

	return (
		<div className={`progress-ring ${props.classModifier || ''}`}>
			<div className="graphic">
				<div style={{ width: SIZE * 2 + 10, height: SIZE * 2 + 10 }} ref={chartRef}></div>
				{props.text || props.text === 0 ? <div className="text">{props.text}</div> : <></>}
			</div>
			{props.label && <div className="label">{props.label}</div>}
		</div>
	);
});

interface IProgressRingSmall {
	size?: number;
	strokeWidth?: number;
	progress: number;
	color?: string;
}

export const ProgressRingSmall: React.FC<IProgressRingSmall> = observer((props: IProgressRingSmall) => {
	const { size = 25, strokeWidth = 6, color = colors.primary, progress } = props;
	const backgroundColor = colors.border;

	// Calculate dimensions
	const radius = (size - strokeWidth) / 2;
	const circumference = 2 * Math.PI * radius;
	const center = size / 2;

	// Calculate stroke offset
	const clampedProgress = Math.max(0, Math.min(100, progress));
	const strokeDashoffset = circumference - (clampedProgress / 100) * circumference;

	return (
		<svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
			{/* Background circle */}
			<circle cx={center} cy={center} r={radius} fill="none" stroke={backgroundColor} strokeWidth={strokeWidth} />

			{/* Progress circle */}
			<circle
				cx={center}
				cy={center}
				r={radius}
				fill="none"
				stroke={color}
				strokeWidth={strokeWidth}
				strokeDasharray={circumference}
				strokeDashoffset={strokeDashoffset}
				transform={`rotate(-90 ${center} ${center})`}
			/>
		</svg>
	);
});
